import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ApiError, confirmPasswordReset } from "@/lib/api";
import { Button } from "@/components/ui/Button";

/**
 * M9a C2 — `/reset-password?token=` landing.
 *
 * Token from query string auto-populates. Submit POSTs
 * `/api/auth/password-reset/confirm` (anonymous; token IS the auth).
 * On success, auto-redirects to `/login` after 1.2 s so the operator
 * can sign in with the fresh password.
 *
 * Privacy invariant (C3): token + new_password are sent in the
 * mutation body; the token is cleared from local state on success,
 * the password fields are cleared too. URL `?token=` survives the
 * mutation (the browser keeps the query string) but the inputs are
 * normal `<input>` elements — not preserved in the rendered DOM
 * after the success transition.
 */
export function ResetPasswordPage() {
  const search = useSearch({ from: "/reset-password" });
  const navigate = useNavigate({ from: "/reset-password" });
  const [token, setToken] = useState(search.token ?? "");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      confirmPasswordReset({ token: token.trim(), new_password: pw }),
    onSuccess: () => {
      setSuccess(true);
      setToken("");
      setPw("");
      setConfirm("");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "That reset token is invalid or expired.",
        );
      } else {
        setError(formatErr(err));
      }
    },
  });

  // On success, auto-redirect to /login after 1.2 s. The legacy
  // does the same — operator sees the confirmation copy briefly,
  // then lands on the login form ready to sign in with the new
  // password.
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => {
      void navigate({ to: "/login", replace: true });
    }, 1200);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  return (
    <main
      data-testid="reset-password-page"
      className="min-h-screen bg-canvas flex items-center justify-center p-4"
    >
      <section
        aria-label="Reset your password"
        className="w-full max-w-md rounded border border-rule bg-surface p-6 flex flex-col gap-3"
      >
        <header>
          <h1 className="text-xl font-display tracking-tight text-ink">
            Reset password
          </h1>
          <p className="text-ink-muted text-xs">
            Paste the reset token from your email and choose a new password.
          </p>
        </header>

        {success ? (
          <p
            data-testid="reset-password-success"
            className="text-good text-sm font-mono"
          >
            Password reset. Sign in with your new password…
          </p>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              if (token.trim().length < 16) {
                setError("Reset token must be at least 16 characters.");
                return;
              }
              if (pw.length < 8) {
                setError("New password must be at least 8 characters.");
                return;
              }
              if (pw !== confirm) {
                setError("New password and confirmation don't match.");
                return;
              }
              mut.mutate();
            }}
            className="flex flex-col gap-2"
          >
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-display uppercase tracking-wider text-ink-muted">
                Reset token
              </span>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                autoFocus
                data-testid="reset-password-token"
                className="rounded border border-rule bg-canvas px-2 py-1.5 text-xs font-mono text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-display uppercase tracking-wider text-ink-muted">
                New password
              </span>
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoComplete="new-password"
                data-testid="reset-password-new"
                className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-display uppercase tracking-wider text-ink-muted">
                Confirm new password
              </span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="reset-password-confirm"
                className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>

            {error ? (
              <p
                data-testid="reset-password-error"
                className="text-destructive text-xs font-mono"
              >
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              size="sm"
              disabled={mut.isPending}
              data-testid="reset-password-submit"
              className="self-start"
            >
              {mut.isPending ? "Resetting…" : "Reset password"}
            </Button>
          </form>
        )}
      </section>
    </main>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string"
      ? err.detail
      : `HTTP ${err.status}`;
  }
  return "Network error. Try again.";
}
